import { AlertTriangle, ChevronRight, HeartHandshake } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { CsHealthBadge } from '@/components/Cs/CsHealthBadge'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorListTableShell,
  GestorTableAvatar,
} from '@/components/Gestores/GestorListTableShell'
import { CS_STAGE_LABELS, type CsAccountSummary, type CsCarteiraStage } from '@/services/csService'
import { cn } from '@/utils/cn'

const STAGE_BADGE_CLASS: Record<CsCarteiraStage, string> = {
  CLIENTE_ATIVO: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  EM_ACOMPANHAMENTO: 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border-[var(--brand-primary)]/20',
  ATENCAO: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  RISCO: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  CRITICO: 'bg-red-500/10 text-red-400 border-red-500/20',
  PLANO_ACAO: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  RETENCAO: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
}

function accountDisplayName(acc: CsAccountSummary): string {
  return acc.empresa.nomeFantasia || acc.empresa.razaoSocial || acc.empresa.nome || 'Cliente'
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

type Props = {
  accounts: CsAccountSummary[]
  loading?: boolean
}

export function CsCarteiraTable({ accounts, loading }: Props) {
  const navigate = useNavigate()

  return (
    <GestorListTableShell
      loading={loading}
      loadingMessage='Carregando carteira…'
      empty={!loading && accounts.length === 0}
      emptyIcon={
        <HeartHandshake size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
      }
      emptyTitle='Nenhuma conta na carteira'
      emptyHint='Contas entram automaticamente quando um deal é ganho.'
    >
      <table className='w-full min-w-[1100px]'>
        <thead>
          <tr className='border-b border-[var(--glass-border)]'>
            {(
              [
                { key: 'cliente', label: 'Cliente' },
                { key: 'projeto', label: 'Projeto' },
                { key: 'classe', label: 'Classe' },
                { key: 'estagio', label: 'Estágio', className: 'min-w-[9.5rem] whitespace-nowrap' },
                { key: 'csm', label: 'CSM' },
                { key: 'saude', label: 'Saúde' },
                { key: 'prox', label: 'Próx. reunião', className: 'whitespace-nowrap' },
                { key: 'risco', label: 'Risco' },
                { key: 'actions', label: '' },
              ] as Array<{ key: string; label: string; className?: string }>
            ).map((col) => (
              <th
                key={col.key}
                className={cn(GESTOR_TABLE_TH_CLASS, col.className)}
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {accounts.map((acc) => {
            const name = accountDisplayName(acc)
            return (
              <tr
                key={acc.id}
                className={cn(
                  GESTOR_TABLE_TR_CLASS,
                  acc.riscoChurn && 'bg-rose-500/[0.03]',
                )}
                onClick={() => navigate(`/cs/contas/${acc.id}`)}
              >
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <GestorTableAvatar name={name} />
                    <div className='min-w-0'>
                      <span
                        className='block truncate font-medium text-[var(--font-color-body)]'
                        style={{ fontSize: 'var(--font-size-sm)' }}
                      >
                        {name}
                      </span>
                      {acc.empresa.cnpj && (
                        <span className='mt-0.5 block font-mono text-[11px] text-[var(--font-color-muted)]'>
                          {acc.empresa.cnpj}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {acc.deal.nome}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {acc.classificacao ?? '—'}
                  </span>
                </td>
                <td className='min-w-[9.5rem] whitespace-nowrap px-4 py-3'>
                  <span
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-[10px] font-semibold',
                      STAGE_BADGE_CLASS[acc.csStage] ??
                        'border-white/10 bg-white/[0.06] text-[var(--text-muted)]',
                    )}
                  >
                    {CS_STAGE_LABELS[acc.csStage] ??
                      String(acc.csStage || '—').replace(/_/g, ' ')}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {acc.csm?.name ?? '—'}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <CsHealthBadge score={acc.healthScoreFinal ?? acc.healthScore} band={acc.healthBand} compact />
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {formatDate(acc.proximaReuniao)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  {acc.riscoChurn ? (
                    <span className='inline-flex items-center gap-1 rounded-md border border-rose-500/20 bg-rose-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-rose-400'>
                      <AlertTriangle size={10} /> Churn
                    </span>
                  ) : (
                    <span
                      className='text-[var(--font-color-muted)]'
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      —
                    </span>
                  )}
                </td>
                <td className='px-4 py-3'>
                  <ChevronRight size={14} className='text-[var(--text-muted)]' aria-hidden />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </GestorListTableShell>
  )
}
